import React, { useState, useRef, ReactElement, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Tooltip,
  Box
} from "@mui/material";
import RenderTooltipContent from "./RenderTooltipContent";
import "./CustomTablewithTotalInfo.css";
import { truncateText } from "../../features/stocks/RealTimeStock/RealTimeStockUtils";
import { truncateToTwoDecimals } from "./DmsUtils";

interface Column {
  currencySymbol?: boolean;
  headerName: string;
  mappedValue: string;
  textAlign?: string;
  width?: number;
  renderCell?: (value: any, row: any) => React.ReactNode;
  renderHeader?: (value: any) => React.ReactNode;
  truncateTextAfterChar?: number;
}

interface CustomTableProps {
  columns: Column[];
  data: any[];
  totals: any[];
  children?: ReactElement;
  headerBackgroundColor?: string;
  isEditing?: boolean;
  isArs?: boolean;
  setScrollTop?: React.Dispatch<React.SetStateAction<number>>;
  isScrolling?: boolean
  allowTableScroll?: boolean;
}

const CustomTablewithTotalInfo: React.FC<CustomTableProps> = ({
  columns,
  data,
  totals,
  children,
  headerBackgroundColor,
  isEditing,
  isArs,
  setScrollTop = () => {},
  isScrolling,
  allowTableScroll = true
}) => { const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const currencySymbol = ConfigDictionary.currency;
  const designationBasedUomSecondary = ConfigDictionary.designationBasedUomSecondary;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = data?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];
  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);
  function isNumeric(value: unknown): value is number | string {
    return (
      (typeof value === "string" || typeof value === "number") &&
      !isNaN(Number(value))
    );
  }

  if(designationEnabled && designation && designationKeys && matchedRole){
    const isCaseAllowed = matchedRole?.uomAllowed?.case?.trim();

    const elementsToRemove = isCaseAllowed
    ? ["pieceQuantity", "otherUnitQuantity"]
    : ["caseQuantity", "otherUnitQuantity"];

    columns = columns.filter(element => !elementsToRemove.includes(element.mappedValue));

    // const elementToKeep = matchedRole?.uomAllowed?.case?.trim() ? "Cs" : "Pc";
    
    // const column = columns.find(col => col.mappedValue === 'price');

    // if (column) {
    //   column.headerName = `Price (Each ${elementToKeep})`; 
    // }
  }

  const renderSkusCell = (skus: string[] = []) => {
    if (skus.length === 0) {
      return "-";
    }

    return (
      <Tooltip
        title={<RenderTooltipContent items={skus} title="SKU Code & Name" />}
        placement="right"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#FFFFFF !important",
              color: "#000000 !important",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
            },
          },
          arrow: {
            sx: {
              color: "#FFFFFF !important",
            },
          },
        }}
      >
        <div style={{ cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {skus[0]?.slice(0, 25)}…
          {skus.length > 1 && (
            <span style={{ color: "#2568E5", textDecoration: "underline" }}>
              +{skus.length - 1}
            </span>
          )}
        </div>
      </Tooltip>
    );
  };


const tableref = useRef<HTMLTableSectionElement>(null);
useEffect(() => {
  const handleScrollEnd = () => {
    if (setScrollTop && tableref.current) {
      const scrollTopValue = tableref.current.scrollTop;
      setScrollTop(scrollTopValue);
    }
  }
  
  const el = tableref.current;
  if (el) {
    el.addEventListener("scrollend", handleScrollEnd)
  }

  return () => {
    if (el) {
      el.removeEventListener("scrollend", handleScrollEnd);
    }
  };
}, [setScrollTop, isScrolling]);

  return (
    <>
    <div className="custom_table_wrapper ignore-scroll-detection">
      <TableContainer
        sx={{
          overflowY:  allowTableScroll ? "auto" : "hidden",
          height: "100%",
          maxHeight: "100%",
          scrollbarWidth: ConfigDictionary?.showTableScrollbar ? "thin" : "none",
          ...(!(ConfigDictionary?.showTableScrollbar) && {
            "&::-webkit-scrollbar": {
              display: "none",
            },
          })
        }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: columns.reduce(
              (sum: any, column: any) => sum + (column.width || 0),
              0
            ),
            position: "relative",
            borderCollapse: "collapse",
            height: "100%",
          }}
        >
          <TableHead style={{ position: "sticky", top: 0, zIndex: 3 }}>
            <TableRow
              style={{
                position: "sticky",
                top: 0,
                border: `1px solid ${headerBackgroundColor}`,
                backgroundColor: headerBackgroundColor || "#ffffff",
                zIndex: 2,
                display: "flex",
                justifyContent: "space-around",
              }}
            >
              {columns.map((col: any, index: number) => (
                <TableCell
                  key={col.headerName + index}
                  className="custom_table_header"
                  sx={{
                    borderBottom: "0px !important",
                    textAlign: `${col?.textAlign || "left"}`,
                  }}
                  style={{
                    width: col.width,
                    minWidth: `${col.width}px`,
                    maxWidth: `${col.width}px`,
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                    alignContent: "center",
                    backgroundColor: headerBackgroundColor || "#ffffff",
                  }}
                >
                  {col.renderHeader
                    ? col.renderHeader(col.headerName)
                    : col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            ref={tableref}
            sx={{
              scrollbarWidth: (ConfigDictionary?.showTableScrollbar ?? false) ? "thin" : "none",
              overflowY:  allowTableScroll ? "auto" : "hidden",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {data.length === 0 ? ( 
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{
                    textAlign: "center",
                    borderBottom: "none",
                    height: "200px",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ fontSize: "14px", color: "#7C7C7C" }}
                  >
                    No data found.
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
            paginatedData.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                style={{
                  borderBottom: rowIndex === paginatedData.length - 1 ? "none" : "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-around",
                  // background: row?.source?.toLowerCase() === "ars" ? "rgb(238, 238, 238)" : "transparent",
                }}
              >
                {columns?.map((col, index) => {
                  let truncatedText = col?.renderCell
                    ? ""
                    : truncateText(
                        row[col.mappedValue]?.toString(),
                        col?.truncateTextAfterChar ?? +Infinity
                      );

                  let inputText = truncatedText; // your original string input
                  let formattedValue;
                  if (col?.currencySymbol) {
                    truncatedText = `${currencySymbol}\u200A${truncatedText ?? ""}`;
                    if (isNumeric(inputText)) {
                        formattedValue = truncateToTwoDecimals(Number(inputText));
                    } else {
                        formattedValue = inputText;
                    }
                  }
                  truncatedText = `${currencySymbol}\u200A${formattedValue ?? ""}`;

                  const isEllipsisColumn = col.mappedValue === "skuName" || col.mappedValue === "outletName" || col.mappedValue === "outletCode" || col.mappedValue === "remark";
                  if (col.mappedValue === "skus") {
                    return (
                      <TableCell
                        sx={{
                          alignContent: "center",
                          width: `${col.width}px`,
                          minWidth: `${col.width}px`,
                          maxWidth: `${col.width}px`,
                          textAlign: `${col?.textAlign || "left"}`,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          border: "0px !important",
                          color: "#7C7C7C",
                          fontSize: "13px",
                        }}
                        key={index}
                        className="custom_table_cell"
                      >
                        {renderSkusCell(row.skus)}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell
                      sx={{
                        alignContent: "center",
                        width: `${col.width}px`,
                        minWidth: `${col.width}px`,
                        maxWidth: `${col.width}px`,
                        textAlign: `${col?.textAlign || "left"}`,
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: isEllipsisColumn ? "nowrap" : "normal", 
                        overflow: isEllipsisColumn ? "hidden" : "visible", 
                        textOverflow: isEllipsisColumn ? "ellipsis" : "clip", 
                        border: "0px !important",
                        color: "#7C7C7C",
                        fontSize: "13px",
                      }}
                      key={index}
                      className="custom_table_cell"
                    >
                      {col.renderCell ? (
                        col.renderCell(row[col.mappedValue], row)
                      ) : isEllipsisColumn ? (
                          <Tooltip title={row[col.mappedValue]?.toString()} arrow>
                            <span
                              style={{
                                display: "inline-block",
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {truncatedText}
                            </span>
                          </Tooltip>
                      ) : (
                        truncatedText
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            )))}
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{ padding: "20px 0", borderBottom: "none" }}
              />
            </TableRow>
          </TableBody>
        {totals?.length != 0 && !isEditing ? (
          <TableFooter
            sx={{
              width: "100%",
              background: "#fafbfc", // Lighter gray background for softer appearance
              zIndex: 2,
              borderTop: "0.5px solid #e9ecef", // Slightly darker border for better definition
              position: "sticky",
            }}
          >
            <TableRow
              style={{
                display: "flex",
                justifyContent: "space-around",
              }}
            >
              {columns.map((col, index) => {

                const value = totals.find(
                  (total: any) => total?.headerName === col?.headerName
                )?.total ?? ""
                
                const adjustedFontSize = (String(value)?.length + String(currencySymbol ?? ``)?.length) > 9 ? "12px !important" : "14px !important"

                return (
                  <TableCell
                    key={index}
                    sx={{
                      alignContent: "center",
                      textAlign: `${
                        totals.find(
                          (total: any) => total?.headerName === col?.headerName
                        )?.textAlign || "left"
                      }`,
                      width: `${col.width}px`,
                      maxWidth: `${col.width}px`,
                      minWidth: `${col.width}px`,
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      border: "0px !important",
                      color: "#5a6c7d", // Lighter blue-gray text for softer appearance
                      fontWeight: "600",
                    }}
                    className="custom_table_footer_cell"
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {totals.find(
                        (total: any) => total?.headerName === col?.headerName
                      )?.currencySymbol
                        ? currencySymbol
                        : ""}{`\u200A`}
                      {totals.find(
                        (total: any) => total?.headerName === col?.headerName
                      )?.total ?? ""}
                    </span>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        ) : (
          <></>
        )}
      </Table>
      </TableContainer>
    </div>

    <div className="custom_table_footer_container">
      <TablePagination
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          width: "max-content",
          position: "sticky",
          zIndex: 3,
          bottom: 0,
          "& p": {
            margin: 0,
          },
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {children}
    </div>
  </>
  );
};

export default CustomTablewithTotalInfo;
